import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { LeadFormData, formatPhone, validateWhatsApp } from "@/lib/index";
import { sendToGoogleSheets, getRevenueLabel, getCurrentTimestamp } from "@/lib/googleSheets";

interface LeadFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LeadFormModal({ open, onOpenChange }: LeadFormModalProps) {
  const [formData, setFormData] = useState<LeadFormData>({
    fullName: "",
    whatsapp: "",
    instagram: "",
    revenueRange: "none"
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});

  const revenueOptions = [
    { value: "none", label: "Selecione sua faixa de faturamento" },
    { value: "0-50k", label: "Até R$ 50.000/mês" },
    { value: "50k-100k", label: "R$ 50.000 - R$ 100.000/mês" },
    { value: "100k-250k", label: "R$ 100.000 - R$ 250.000/mês" },
    { value: "250k-500k", label: "R$ 250.000 - R$ 500.000/mês" },
    { value: "500k+", label: "Acima de R$ 500.000/mês" }
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Nome completo é obrigatório";
    } else if (formData.fullName.trim().split(" ").length < 2) {
      newErrors.fullName = "Por favor, insira seu nome completo";
    }

    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = "WhatsApp é obrigatório";
    } else if (!validateWhatsApp(formData.whatsapp)) {
      newErrors.whatsapp = "WhatsApp inválido. Use formato: (XX) XXXXX-XXXX";
    }

    if (!formData.instagram.trim()) {
      newErrors.instagram = "Instagram é obrigatório";
    } else if (!formData.instagram.startsWith("@")) {
      newErrors.instagram = "Instagram deve começar com @";
    }

    if (formData.revenueRange === "none") {
      newErrors.revenueRange = "Selecione sua faixa de faturamento";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Preparar dados para envio
      const leadData = {
        timestamp: getCurrentTimestamp(),
        fullName: formData.fullName,
        whatsapp: formData.whatsapp,
        instagram: formData.instagram,
        revenueRange: formData.revenueRange,
        revenueLabel: getRevenueLabel(formData.revenueRange),
        source: 'Landing Page - Método Êxodo',
      };

      // Enviar para Google Sheets
      const success = await sendToGoogleSheets(leadData);
      
      if (success) {
        setSuccess(true);
        
        setTimeout(() => {
          setSuccess(false);
          onOpenChange(false);
          setFormData({
            fullName: "",
            whatsapp: "",
            instagram: "",
            revenueRange: "none"
          });
        }, 2000);
      } else {
        setError("Erro ao enviar formulário. Tente novamente.");
      }
    } catch (err) {
      console.error('Erro no envio:', err);
      setError("Erro ao enviar formulário. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppChange = (value: string) => {
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, whatsapp: formatted }));
    if (errors.whatsapp) {
      setErrors(prev => ({ ...prev, whatsapp: undefined }));
    }
  };

  const handleInstagramChange = (value: string) => {
    let formatted = value;
    if (value && !value.startsWith("@")) {
      formatted = "@" + value;
    }
    setFormData(prev => ({ ...prev, instagram: formatted }));
    if (errors.instagram) {
      setErrors(prev => ({ ...prev, instagram: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Garanta Sua Vaga
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Preencha o formulário abaixo para participar do Método Êxodo
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 mx-auto text-primary" />
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Cadastro Realizado!
              </h3>
              <p className="text-muted-foreground">
                Em breve entraremos em contato via WhatsApp.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-foreground">
                Nome Completo *
              </Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Digite seu nome completo"
                value={formData.fullName}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, fullName: e.target.value }));
                  if (errors.fullName) {
                    setErrors(prev => ({ ...prev, fullName: undefined }));
                  }
                }}
                className="bg-muted border-border focus:border-primary"
                disabled={loading}
              />
              {errors.fullName && (
                <p className="text-sm text-destructive">{errors.fullName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp" className="text-foreground">
                WhatsApp *
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                placeholder="(XX) XXXXX-XXXX"
                value={formData.whatsapp}
                onChange={(e) => handleWhatsAppChange(e.target.value)}
                className="bg-muted border-border focus:border-primary"
                disabled={loading}
              />
              {errors.whatsapp && (
                <p className="text-sm text-destructive">{errors.whatsapp}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagram" className="text-foreground">
                Instagram *
              </Label>
              <Input
                id="instagram"
                type="text"
                placeholder="@seuinstagram"
                value={formData.instagram}
                onChange={(e) => handleInstagramChange(e.target.value)}
                className="bg-muted border-border focus:border-primary"
                disabled={loading}
              />
              {errors.instagram && (
                <p className="text-sm text-destructive">{errors.instagram}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="revenueRange" className="text-foreground">
                Faixa de Faturamento *
              </Label>
              <Select
                value={formData.revenueRange}
                onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, revenueRange: value }));
                  if (errors.revenueRange) {
                    setErrors(prev => ({ ...prev, revenueRange: undefined }));
                  }
                }}
                disabled={loading}
              >
                <SelectTrigger className="bg-muted border-border focus:border-primary">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {revenueOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.revenueRange && (
                <p className="text-sm text-destructive">{errors.revenueRange}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-primary to-accent hover:scale-105 transition-transform duration-200 text-primary-foreground font-semibold"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Garantir Minha Vaga"
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}